import React, { useEffect, useState } from "react";
import {
  Gift,
  Coins,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Save,
  Loader2,
  Users,
  TrendingUp,
  RefreshCw,
  Eye,
  Send,
} from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

export default function SignupReward() {
  const [setting, setSetting] = useState({
    rewardCoins: 50,
    isEnabled: true,
    welcomeMessage: "Congratulations! You have earned {coins} coins on your first signup. Enjoy it!",
  });
  const [stats, setStats] = useState({
    totalRewardedUsers: 0,
    totalCoinsDistributed: 0,
    totalTransactions: 0,
  });
  const [recentRewards, setRecentRewards] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Postman test simulator state
  const [testPhone, setTestPhone] = useState("+919876543299");
  const [testName] = useState("Test Signup User");
  const [testUsername, setTestUsername] = useState("testuser_" + Math.floor(1000 + Math.random() * 9000));
  const [testingApi, setTestingApi] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchSettingAndStats = async () => {
    try {
      setLoading(true);
      setError("");
      
      const [settingRes, statsRes] = await Promise.all([
        API.get("/admin/signup-reward"),
        API.get("/admin/signup-reward/stats"),
      ]);

      if (settingRes.data?.success && settingRes.data?.setting) {
        setSetting({
          rewardCoins: settingRes.data.setting.rewardCoins ?? 50,
          isEnabled: settingRes.data.setting.isEnabled ?? true,
          welcomeMessage:
            settingRes.data.setting.welcomeMessage ||
            "Congratulations! You have earned {coins} coins on your first signup. Enjoy it!",
        });
      }

      if (statsRes.data?.success) {
        setStats(statsRes.data.stats || { totalRewardedUsers: 0, totalCoinsDistributed: 0, totalTransactions: 0 });
        setRecentRewards(statsRes.data.recentRewards || []);
      }
    } catch (err) {
      console.error("Failed to load signup reward data:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load signup reward settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettingAndStats();
  }, []);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === recentRewards.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(recentRewards.map((r) => r._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "coinRecords",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccessMsg("Selected reward records deleted successfully");
        fetchSettingAndStats();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError("");
      setSuccessMsg("");

      const response = await API.put("/admin/signup-reward", {
        rewardCoins: Number(setting.rewardCoins),
        isEnabled: Boolean(setting.isEnabled),
        welcomeMessage: setting.welcomeMessage,
      });

      if (response.data?.success) {
        setSuccessMsg("Signup reward configuration saved successfully!");
        if (response.data.setting) {
          setSetting({
            rewardCoins: response.data.setting.rewardCoins,
            isEnabled: response.data.setting.isEnabled,
            welcomeMessage: response.data.setting.welcomeMessage,
          });
        }
        setTimeout(() => setSuccessMsg(""), 4000);
      }
    } catch (err) {
      console.error("Failed to save setting:", err);
      setError(err?.response?.data?.message || err.message || "Failed to update signup reward setting");
    } finally {
      setSaving(false);
    }
  };

  const handleTestSignupApi = async () => {
    try {
      setTestingApi(true);
      setTestResult(null);
      
      const payload = {
        phone: testPhone,
        name: testName,
        username: testUsername,
        age: "25",
        gender: "Male",
        bio: "Testing signup reward API feature",
      };

      const res = await API.post("/user/profile-info", payload);
      setTestResult({
        status: res.status,
        data: res.data,
      });

      // Refresh stats
      fetchSettingAndStats();
    } catch (err) {
      setTestResult({
        status: err?.response?.status || 500,
        data: err?.response?.data || { message: err.message },
      });
    } finally {
      setTestingApi(false);
    }
  };

  const previewMessage = setting.welcomeMessage
    ? setting.welcomeMessage.replace("{coins}", setting.rewardCoins || 0)
    : `Congratulations! You have earned ${setting.rewardCoins || 0} coins. Enjoy it!`;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500 dark:text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-3" />
        <p className="text-sm font-medium">Loading Signup Reward Configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Signup Reward Settings</h1>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                setting.isEnabled
                  ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20"
                  : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${setting.isEnabled ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
              {setting.isEnabled ? "Feature Active" : "Feature Disabled"}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Customize free reward coins and welcome messages given automatically to new users on first signup.
          </p>
        </div>

        <button
          onClick={fetchSettingAndStats}
          className="self-start sm:self-auto flex items-center gap-2 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Stats
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 shrink-0 text-red-500" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-green-500" />
          <span className="text-sm font-medium">{successMsg}</span>
        </div>
      )}

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Reward Per Signup
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {setting.rewardCoins} <span className="text-sm font-normal text-amber-500">Coins</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Gift className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
            <span>Status: </span>
            <strong className={`ml-1 ${setting.isEnabled ? "text-green-500" : "text-red-500"}`}>
              {setting.isEnabled ? "Active & Giving Rewards" : "Paused"}
            </strong>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Rewarded Users
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalRewardedUsers}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
            <span>Claimed signup reward coins</span>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Total Distributed
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalCoinsDistributed.toLocaleString()}{" "}
                <span className="text-sm font-normal text-amber-500">Coins</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
              <Coins className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
            <span>Total promotional budget used</span>
          </div>
        </div>
      </div>

      {/* Configuration & Preview Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Settings Form */}
        <div className="lg:col-span-7 bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-6 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-slate-700 pb-4 mb-6">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
              Configure Reward Parameters
            </h2>
          </div>

          <form onSubmit={handleSave} className="space-y-5">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-slate-900/50 border border-gray-200/60 dark:border-slate-700/60">
              <div>
                <label className="text-sm font-semibold text-gray-800 dark:text-gray-200 block">
                  Enable Signup Reward Feature
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  When enabled, every new user completing signup receives free wallet coins.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSetting((prev) => ({ ...prev, isEnabled: !prev.isEnabled }))}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  setting.isEnabled ? "bg-green-500" : "bg-gray-300 dark:bg-slate-600"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    setting.isEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Coins Input */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-between">
                <span>Reward Coins Amount</span>
                <span className="text-xs text-amber-500 font-semibold">Credited to User Wallet</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  value={setting.rewardCoins}
                  onChange={(e) => setSetting((prev) => ({ ...prev, rewardCoins: e.target.value }))}
                  placeholder="e.g. 50"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pl-11"
                  required
                />
                <Coins className="w-5 h-5 text-amber-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Welcome Message Input */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-between">
                <span>Custom Reward Message</span>
                <span className="text-xs text-gray-400">Use <code className="text-blue-400">{`{coins}`}</code> placeholder</span>
              </label>
              <div className="relative">
                <textarea
                  rows={3}
                  value={setting.welcomeMessage}
                  onChange={(e) => setSetting((prev) => ({ ...prev, welcomeMessage: e.target.value }))}
                  placeholder="e.g. You have earned {coins} coins! Enjoy it!"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This message will be included in the user's signup API response and transaction note.
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving Changes...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Live Preview Card */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-6 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-slate-700 pb-4 mb-5">
              <Eye className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                Live User Experience Preview
              </h2>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              This is how the user sees the reward notification pop-up upon completion of signup:
            </p>

            {/* User Popup Mockup */}
            <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 text-white shadow-xl border border-purple-500/30">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-amber-500/20 rounded-full blur-xl pointer-events-none" />
              
              <div className="flex flex-col items-center text-center space-y-3 relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-amber-400 to-amber-200 text-slate-900 flex items-center justify-center shadow-lg shadow-amber-500/30 animate-bounce">
                  <Gift className="w-9 h-9 text-slate-900" />
                </div>

                <div className="space-y-1">
                  <span className="inline-block px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-400/20 text-amber-300 border border-amber-400/30">
                    Welcome Gift
                  </span>
                  <h3 className="text-xl font-extrabold text-white">
                    +{setting.rewardCoins} Reward Coins!
                  </h3>
                </div>

                <p className="text-sm text-purple-100 leading-relaxed bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10 w-full">
                  "{previewMessage}"
                </p>

                <div className="w-full pt-2">
                  <button
                    type="button"
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 hover:opacity-95"
                  >
                    Enjoy It & Start Chatting
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Postman API Test Tool */}
          <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-indigo-500" />
                <h3 className="text-md font-semibold text-gray-800 dark:text-white">
                  Postman API Test Helper
                </h3>
              </div>
              <span className="text-[11px] font-mono bg-indigo-500/10 text-indigo-500 px-2 py-0.5 rounded">
                POST /user/profile-info
              </span>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Test user signup reward execution directly from this panel:
            </p>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Phone"
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-gray-200"
                />
                <input
                  type="text"
                  value={testUsername}
                  onChange={(e) => setTestUsername(e.target.value)}
                  placeholder="Username"
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-gray-200"
                />
              </div>

              <button
                type="button"
                onClick={handleTestSignupApi}
                disabled={testingApi}
                className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
              >
                {testingApi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Simulate Postman Signup Request
              </button>

              {testResult && (
                <div className="mt-3 p-3 rounded-xl bg-slate-900 text-xs font-mono text-green-400 max-h-40 overflow-y-auto border border-slate-700">
                  <div className="text-gray-400 text-[10px] mb-1">Status Code: {testResult.status}</div>
                  <pre className="whitespace-pre-wrap">{JSON.stringify(testResult.data, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Rewards Log Table */}
      <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-gray-200/80 dark:border-slate-700/80 p-6 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-4 mb-5">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
              Recent Signup Reward Transactions
            </h2>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Showing latest {recentRewards.length} transactions
          </span>
        </div>

        {recentRewards.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No signup reward transactions recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wider">
                  <th className="py-3 px-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={recentRewards.length > 0 && selectedIds.length === recentRewards.length}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-3.5 xl:px-4 2xl:px-6">User</th>
                  <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Phone</th>
                  <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Coins Rewarded</th>
                  <th className="py-3 px-3.5 xl:px-4 2xl:px-6">Date & Time</th>
                  <th className="py-3 px-3.5 xl:px-4 2xl:px-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50 text-sm">
                {recentRewards.map((tx) => {
                  const isSelected = selectedIds.includes(tx._id);
                  return (
                    <tr key={tx._id} className={`transition-colors ${isSelected ? "bg-blue-50/70 dark:bg-blue-500/10" : "hover:bg-gray-50/50 dark:hover:bg-slate-700/30"}`}>
                      <td className="py-3.5 px-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(tx._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-medium text-gray-900 dark:text-white flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {tx.toUser?.name ? tx.toUser.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 dark:text-gray-200">
                            {tx.toUser?.name || "Unknown User"}
                          </div>
                          <div className="text-xs text-gray-400">@{tx.toUser?.username || "user"}</div>
                        </div>
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-gray-600 dark:text-gray-300 font-mono text-xs">
                        {tx.toUser?.phone || "-"}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-bold text-amber-500">
                        +{tx.amount} Coins
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(tx.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-right">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Success
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={recentRewards.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="reward records"
      />
    </div>
  );
}
